/**
 * Ruta protegida — Guard de autenticación y permisos para rutas privadas
 * Exporta: default export
 */
import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import type { Permission } from "@/types";
import { CASHIER_ROLES, OPERATIVE_ROLES, type Role } from "@/config/roles";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Role[];
  requiredModule?: keyof Permission;
}

/**
 * Componente de ruta protegida.
 * Verifica autenticación, roles y permisos modulares antes de renderizar.
 * Espera la rehidratación de Zustand persist para no redirigir prematuramente.
 */
const ProtectedRoute = ({
  children,
  allowedRoles,
  requiredModule,
}: ProtectedRouteProps) => {
  const { isAuthenticated, user, token } = useAuthStore();
  const location = useLocation();

  // Esperar rehidratación de Zustand persist antes de tomar decisiones de auth
  const [hydrated, setHydrated] = useState(
    useAuthStore.persist?.hasHydrated?.() ?? true,
  );

  useEffect(() => {
    if (hydrated) return;
    const unsub = useAuthStore.persist.onFinishHydration(() =>
      setHydrated(true),
    );
    return unsub;
  }, [hydrated]);

  if (!hydrated) {
    return null; // Render nada mientras Zustand carga de localStorage
  }

  // 1. Verificar autenticación básica
  if (!isAuthenticated || !token || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 2. Verificar roles si se especifican
  // Excepción: si el usuario tiene permiso en el módulo requerido, omitir
  // el check de roles → acceso controlado por permisos, no solo por rol.
  const grantedByPermission =
    !!requiredModule &&
    (user.permissions?.[requiredModule] ?? "none") !== "none";

  if (
    allowedRoles &&
    !allowedRoles.includes(user.role as Role) &&
    !grantedByPermission
  ) {
    if (CASHIER_ROLES.includes(user.role as Role)) {
      return <Navigate to="/caja" replace />;
    }
    if (OPERATIVE_ROLES.includes(user.role as Role)) {
      return <Navigate to="/mis-tareas" replace />;
    }
    // Fallback seguro para cualquier otro rol no listado
    return <Navigate to="/mis-tareas" replace />;
  }

  // 3. Verificar permisos modulares (el dueño siempre tiene acceso total)
  if (requiredModule && user.permissions && user.role !== "dueño") {
    if (user.permissions[requiredModule] === "none") {
      return <Navigate to="/forbidden" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
